import React, { useCallback, useEffect, useState } from "react";
import { listarDiplomasPromocion } from "../../../../services/promocionesService";
import {
  generarDiplomaPorMatricula,
  descargarDiplomaPorMatricula,
  descargarDiplomasPorPromocion
} from "../../../../services/diplomasService";
import { showToast } from "../../../../utils/showToast";

const buildNombre = (row) => {
  const nombre = [row.apellidos, row.nombres].filter(Boolean).join(" ").trim();
  return nombre || `Estudiante #${row.estudiante_id || "-"}`;
};

const sortByApellidoNombre = (list = []) =>
  [...list].sort((a, b) => {
    const apellidosA = String(a?.apellidos || "").trim();
    const apellidosB = String(b?.apellidos || "").trim();
    const apellidoCompare = apellidosA.localeCompare(apellidosB, "es", { sensitivity: "base" });
    if (apellidoCompare !== 0) return apellidoCompare;
    const nombresA = String(a?.nombres || "").trim();
    const nombresB = String(b?.nombres || "").trim();
    return nombresA.localeCompare(nombresB, "es", { sensitivity: "base" });
  });

const getMatriculaId = (row) => row?.matricula_id || row?.matriculaId;
const tieneDiploma = (row) => Boolean(row?.diploma_archivo);

export default function PromocionCertificadosTab({ promocionId }) {
  const [aprobados, setAprobados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generatingGeneral, setGeneratingGeneral] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadAprobados = useCallback(async () => {
    if (!promocionId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listarDiplomasPromocion(promocionId);
      setAprobados(sortByApellidoNombre(Array.isArray(data) ? data : []));
    } catch (err) {
      console.error(err);
      const message = err.message || "No se pudieron cargar los aprobados.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [promocionId]);

  useEffect(() => {
    loadAprobados();
  }, [loadAprobados]);

  const handleGenerarGeneral = useCallback(async () => {
    if (!promocionId || !aprobados.length) return;
    setGeneratingGeneral(true);
    try {
      await descargarDiplomasPorPromocion(promocionId);
      showToast({ type: "success", message: "Diplomas generados y descargados correctamente." });
      await loadAprobados();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo generar el diploma general." });
    } finally {
      setGeneratingGeneral(false);
    }
  }, [promocionId, aprobados.length, loadAprobados]);

  const handleGenerarIndividual = useCallback(async (row) => {
    const matriculaId = getMatriculaId(row);
    if (!matriculaId) {
      showToast({ type: "warning", message: "No se encontro la matricula para este estudiante." });
      return;
    }
    setGeneratingId(matriculaId);
    try {
      await generarDiplomaPorMatricula(matriculaId);
      await descargarDiplomaPorMatricula(matriculaId);
      showToast({ type: "success", message: `Diploma generado y descargado para ${buildNombre(row)}.` });
      await loadAprobados();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo generar el diploma." });
    } finally {
      setGeneratingId(null);
    }
  }, [loadAprobados]);

  const handleDescargarIndividual = useCallback(async (row) => {
    const matriculaId = getMatriculaId(row);
    if (!matriculaId) {
      showToast({ type: "warning", message: "No se encontro la matricula para este estudiante." });
      return;
    }
    setDownloadingId(matriculaId);
    try {
      await descargarDiplomaPorMatricula(matriculaId);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo descargar el diploma." });
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const total = aprobados.length;
  const emptyRows = Math.max(0, 10 - total);
  const todosGenerados = total > 0 && aprobados.every(tieneDiploma);

  return (
    <div className="management-card promociones-table-card">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
        <div>
          <p className="management-label mb-1">Diplomas</p>
          <h5 className="mb-0">Estudiantes aprobados</h5>
          <small className="text-muted">Total aprobados: {total}</small>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadAprobados} disabled={loading}>
            <i className="bi bi-arrow-repeat me-2" />
            Actualizar
          </button>
          <button
            type="button"
            className={`btn btn-sm ${todosGenerados ? "btn-outline-success" : "btn-outline-primary"}`}
            onClick={handleGenerarGeneral}
            disabled={loading || generatingGeneral || !total}
            title={todosGenerados ? "Todos los diplomas ya estan generados, se descargara el PDF combinado" : undefined}
          >
            {generatingGeneral ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            ) : (
              <i className={`bi ${todosGenerados ? "bi-download" : "bi-file-earmark-text"} me-2`} />
            )}
            {generatingGeneral
              ? "Generando..."
              : todosGenerados
                ? "Descargar diploma general"
                : "Generar diploma general"}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between management-alert mb-4">
          <div className="me-3">Diplomas: {error}</div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadAprobados}>
            Reintentar
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <div className="management-table-wrapper">
          <table className="table align-middle management-table promociones-table mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Matricula</th>
                <th>Estudiante</th>
                <th>Documento</th>
                <th>Nota curso</th>
                <th>Resultado</th>
                <th className="text-center">Diploma</th>
              </tr>
            </thead>
            <tbody>
              {aprobados.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-muted py-3">
                    No hay estudiantes aprobados para esta promocion.
                  </td>
                </tr>
              ) : (
                aprobados.map((row, idx) => {
                  const matriculaId = getMatriculaId(row);
                  const isGenerating = generatingId && matriculaId === generatingId;
                  const isDownloading = downloadingId && matriculaId === downloadingId;
                  const yaGenerado = tieneDiploma(row);
                  const disabledComun = generatingGeneral || isGenerating || isDownloading || !matriculaId;
                  return (
                    <tr key={row.estudiante_formacion_id || row.matricula_id || idx}>
                      <td>{idx + 1}</td>
                      <td>{row.codigo_matricula || "-"}</td>
                      <td className="fw-semibold">{buildNombre(row)}</td>
                      <td>{row.documento_identidad || "-"}</td>
                      <td>{row.nota_curso ?? "-"}</td>
                      <td>
                        <span className="badge rounded-pill px-3 py-2 bg-success text-white">
                          Aprobado
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          {yaGenerado ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => handleDescargarIndividual(row)}
                                disabled={disabledComun}
                                aria-label={`Descargar diploma de ${buildNombre(row)}`}
                                title="Descargar diploma"
                              >
                                {isDownloading ? (
                                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                ) : (
                                  <i className="bi bi-download me-1" />
                                )}
                                Descargar
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => handleGenerarIndividual(row)}
                                disabled={disabledComun}
                                aria-label={`Regenerar diploma de ${buildNombre(row)}`}
                                title="Volver a generar el diploma (sobrescribe el actual)"
                              >
                                {isGenerating ? (
                                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                ) : (
                                  <i className="bi bi-arrow-repeat" />
                                )}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => handleGenerarIndividual(row)}
                              disabled={disabledComun}
                              aria-label={`Generar diploma de ${buildNombre(row)}`}
                              title="Generar diploma individual"
                            >
                              {isGenerating ? (
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                              ) : (
                                <i className="bi bi-person-badge me-1" />
                              )}
                              Generar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {aprobados.length > 0 &&
                emptyRows > 0 &&
                Array.from({ length: emptyRows }).map((_, idx) => (
                  <tr key={`aprobados-empty-${idx}`} className="promociones-table-empty-row" aria-hidden="true">
                    <td colSpan={6}>&nbsp;</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="alert alert-info mt-3 mb-0">
        Los diplomas generados se guardan en el sistema y quedan listos para descarga.
      </div>
    </div>
  );
}
